<template>
  <div class="poynting-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$S = E × H$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan"><label>电场 E</label><span class="val">{{ currentE.toFixed(1) }}</span></div>
          <div class="hud-item orange"><label>磁场 B</label><span class="val">{{ currentB.toFixed(1) }}</span></div>
          <div class="hud-item yellow"><label>能流密度 S</label><span class="val">{{ currentS.toFixed(1) }}</span></div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-s"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-e" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
             <marker id="arr-b" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#38bdf8" /></marker>
             <marker id="arr-sp" markerWidth="8" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6 Z" fill="#fbbf24" /></marker>
          </defs>

          <!-- 传播 Z 轴 -->
          <line x1="20" y1="130" x2="380" y2="130" stroke="#1e293b" stroke-width="2" stroke-dasharray="8,4" />
          <text x="360" y="120" fill="#334155" font-size="9" font-weight="900">Z (Propagation)</text>

          <!-- 三维正交场波形 -->
          <path :d="eFieldPath" fill="none" stroke="#f43f5e" stroke-width="2" opacity="0.8" />
          <path :d="bFieldPath" fill="none" stroke="#38bdf8" stroke-width="2" opacity="0.6" />

          <!-- 关键点处的矢量三叉分析 (E, B, S) -->
          <g v-for="x in [100, 200, 300]" :key="x" :transform="`translate(${x}, 130)`">
             <!-- 电场矢量 E (垂直) -->
             <line x1="0" y1="0" x2="0" :y2="-getYVal(x)" stroke="#f43f5e" stroke-width="2.5" marker-end="url(#arr-e)" />
             <!-- 磁场矢量 B (模拟 X 面 45度投影) -->
             <line x1="0" y1="0" :x2="getXVal(x)" :y2="getXVal(x)*0.6" stroke="#38bdf8" stroke-width="2.5" marker-end="url(#arr-b)" />
             <!-- 坡印廷矢量 S (沿 Z 轴传播) -->
             <line x1="0" y1="0" :x2="Math.max(10, getSVal(x))" y2="0" stroke="#fbbf24" stroke-width="5" filter="url(#glow-s)" marker-end="url(#arr-sp)" />
          </g>

          <text x="50" y="40" fill="#f43f5e" font-size="10" font-weight="900">Electric field E</text>
          <text x="50" y="240" fill="#38bdf8" font-size="10" font-weight="900">Magnetic field B</text>
        </svg>

        <div class="interaction-hint">观察：只有 E 和 B 同时在节点交叉时，能量 S 才沿 Z 轴激增</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">辐射强度 (Amplitude)</label>
                 <el-slider v-model="amp" :min="10" :max="80" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">传播频率 (Frequency)</label>
                 <el-slider v-model="freq" :min="1" :max="10" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-lightning"></i>
           <p><strong>核心揭示：</strong>坡印廷矢量确定了电磁波的功率流方向。它永远正交于 $ \mathbf{E} $ 场与 $ \mathbf{B} $ 场构成的平面。在真空中，它的时均值 $ \langle S \rangle $ 描述了辐射强度的物理本质。<strong>方向判定：</strong>右手掌由 E 转向 B，大拇指即为能流 S 的推进路径。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'PoyntingVectorViz',
  data() {
    return {
      amp: 50,
      freq: 4,
      t: 0
    }
  },
  computed: {
    currentE() { return this.amp * Math.sin(this.t * 3); },
    currentB() { return this.amp * 0.7 * Math.sin(this.t * 3); },
    currentS() { return Math.abs(this.currentE * this.currentB) * 0.5; },
    eFieldPath() {
       let points = [];
       for (let x = 20; x <= 380; x += 5) {
          const y = 130 - this.amp * Math.sin(x * 0.05 - this.t * this.freq);
          points.push(`${x},${y.toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    },
    bFieldPath() {
       let points = [];
       for (let x = 20; x <= 380; x += 5) {
          const val = this.amp * 0.7 * Math.sin(x * 0.05 - this.t * this.freq);
          // Standard isometric-like skew for 3D representation
          points.push(`${(x + val*0.5).toFixed(1)},${(130 + val*0.5).toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    }
  },
  methods: {
    getYVal(x) { return this.amp * Math.sin(x * 0.05 - this.t * this.freq); },
    getXVal(x) { return this.amp * 0.7 * Math.sin(x * 0.05 - this.t * this.freq); },
    getSVal(x) {
       const e = this.getYVal(x);
       const b = this.getXVal(x);
       // Energy flow is always positive in wave direction
       return Math.abs(e * b) * 1.5 / (this.amp * 0.1);
    },
    animate() {
       this.t += 0.03;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.poynting-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fff7ed; color: #9a3412; border: 1px solid #ffedd5; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #f43f5e; } &.orange { border-left: 3px solid #38bdf8; } &.yellow { border-left: 3px solid #fbbf24; } } }
.interaction-hint { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; pointer-events: none; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #fffcf0; padding: 10px 14px; border-radius: 10px; border: 1px solid #fef08a; display: flex; align-items: center; gap: 10px; i { color: #ca8a04; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #854d0e; line-height: 1.5; strong { color: #713f12; } } }
</style>
